import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchAdminAuditLogs, friendlyError } from '@/lib/api';
import { toast } from 'sonner';

const ACTION_COLORS = {
  create: 'bg-[var(--ns-green)]/10 text-[var(--ns-green-700)] border-[var(--ns-green)]/30',
  update: 'bg-[var(--ns-bg-2)] text-[var(--ns-text)] border-[var(--ns-border)]',
  status_change: 'bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] border-[var(--ns-gold)]/40',
  publish: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] border-[var(--ns-gold)]/50',
  archive: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border-[var(--ns-border)]',
  cancel: 'bg-[var(--ns-red)]/10 text-[var(--ns-red)] border-[var(--ns-red)]/30',
};

export default function AdminAuditLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchAdminAuditLogs({ page_size: 50 });
        setItems(res.items || []);
      } catch (err) { toast.error(friendlyError(err)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-5xl">
        <header className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--ns-gold-700)] font-semibold">Auditoría</p>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl text-[var(--ns-text)] leading-tight">Audit logs</h1>
          <p className="mt-2 text-sm text-[var(--ns-text-muted)]">Registro de acciones críticas realizadas por administradores.</p>
        </header>
        <Card className="bg-white border border-[var(--ns-border)] rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-6 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--ns-text-muted)]">Sin registros todavía.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[680px]" data-testid="admin-audit-table">
                <thead className="bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Cuándo</th>
                    <th className="text-left px-4 py-3 font-semibold">Acción</th>
                    <th className="text-left px-4 py-3 font-semibold">Entidad</th>
                    <th className="text-left px-4 py-3 font-semibold">Admin</th>
                    <th className="text-left px-4 py-3 font-semibold">Cambios</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((l) => (
                    <tr key={l.id} className="border-t border-[var(--ns-border)]">
                      <td className="px-4 py-3 text-[var(--ns-text-muted)] text-xs whitespace-nowrap">
                        {l.created_at ? new Date(l.created_at).toLocaleString('es-ES') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={`uppercase tracking-wider text-[10px] font-bold border ${ACTION_COLORS[l.action] || ACTION_COLORS.update}`}>{l.action}</Badge>
                      </td>
                      <td className="px-4 py-3 text-[var(--ns-text)]">{l.entity_type} <span className="text-[var(--ns-text-subtle)] text-xs font-mono">{(l.entity_id || '').slice(0, 8)}</span></td>
                      <td className="px-4 py-3 text-[var(--ns-text-muted)] text-xs">{l.actor_email}</td>
                      <td className="px-4 py-3 text-[var(--ns-text-muted)] text-xs">
                        {l.metadata && Object.keys(l.metadata).length > 0
                          ? <code className="text-[10px]">{JSON.stringify(l.metadata)}</code>
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
